import { useAssetDrop } from '@repo/studio-components-asset-drag/use-asset-drop'
import type { AssetMediaType } from '@repo/studio-components-asset-drag/asset'
import * as React from 'react'
import { useExternalAssetDropStore } from '../stores/external-asset-drop-store'
import { useAidaMediaDragAvailable } from './use-aida-media-drag-available'

const PANEL_MEDIA_DROP: readonly AssetMediaType[] = ['image', 'video']

export function useAidaPanelImageDrop() {
  const [node, setNode] = React.useState<HTMLElement | null>(null)
  const available = useAidaMediaDragAvailable()
  const drop = useAssetDrop({
    accept: PANEL_MEDIA_DROP,
    onDrop(asset) {
      useExternalAssetDropStore.getState().enqueue(asset)
    },
  })
  const dropRef = React.useRef(drop)
  dropRef.current = drop

  React.useEffect(() => {
    if (!node) {
      return
    }
    // The thread is portaled into this shell. React handlers on the shell miss those drops.
    function onDragEnter(event: DragEvent) {
      dropRef.current.onDragEnter(asReactDrag(event))
    }
    function onDragOver(event: DragEvent) {
      dropRef.current.onDragOver(asReactDrag(event))
    }
    function onDragLeave(event: DragEvent) {
      dropRef.current.onDragLeave(asReactDrag(event))
    }
    function onDrop(event: DragEvent) {
      dropRef.current.onDrop(asReactDrag(event))
    }
    node.addEventListener('dragenter', onDragEnter)
    node.addEventListener('dragover', onDragOver)
    node.addEventListener('dragleave', onDragLeave)
    node.addEventListener('drop', onDrop)
    return () => {
      node.removeEventListener('dragenter', onDragEnter)
      node.removeEventListener('dragover', onDragOver)
      node.removeEventListener('dragleave', onDragLeave)
      node.removeEventListener('drop', onDrop)
    }
  }, [node])

  return {
    setDropTarget: setNode,
    showDrop: available || drop.isOver,
    isOver: drop.isOver,
  }
}

function asReactDrag(event: DragEvent) {
  return event as unknown as React.DragEvent<HTMLElement>
}
